/** Shared helpers for checklist item API payloads and option lists */

export interface ChecklistItemLike {
  checklist?: number;
  parent_item?: number | null;
  title: string;
  description?: string;
  item_type: string;
  order: number;
  is_required: boolean;
  validation_rules?: Record<string, unknown>;
  options?: unknown;
  default_value?: string;
  allowed_file_types?: string;
  max_file_size_mb?: number;
  is_visible?: boolean;
  show_when_parent_completed?: boolean;
}

export interface ChecklistItemWritePayload {
  checklist?: number;
  parent_item?: number | null;
  title: string;
  description: string;
  item_type: string;
  order: number;
  is_required: boolean;
  validation_rules: Record<string, unknown>;
  options: string[];
  default_value: string;
  allowed_file_types: string;
  max_file_size_mb: number;
  is_visible: boolean;
  show_when_parent_completed: boolean;
}

export const ITEM_TYPES_WITH_OPTIONS = ['select', 'radio', 'multi_select'] as const;

/** Parse semicolon-separated option values from CSV or text fields */
export function parseOptionsFromCSV(value: string): string[] {
  if (!value || !value.trim()) {
    return [];
  }
  return value
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Normalize options from API/form/CSV into a string array */
export function normalizeChecklistItemOptions(options: unknown): string[] {
  if (Array.isArray(options)) {
    return options.map((o) => String(o).trim()).filter(Boolean);
  }
  if (typeof options === 'string') {
    return parseOptionsFromCSV(options);
  }
  return [];
}

export function hasCannotCompleteReason(reason: unknown): boolean {
  return typeof reason === 'string' && reason.trim().length > 0;
}

export function isCannotCompleteWithoutReason(response: {
  cannot_complete?: boolean;
  cannot_complete_reason?: string;
} | null | undefined): boolean {
  return Boolean(response?.cannot_complete) && !hasCannotCompleteReason(response?.cannot_complete_reason);
}

/**
 * Review-dialog completion check. A normal response counts when it is not null.
 * Cannot Complete counts only after the required reason is filled in.
 */
export function checklistResponseCountsAsDone(response: {
  response?: unknown;
  cannot_complete?: boolean;
  cannot_complete_reason?: string;
} | null | undefined): boolean {
  if (!response) return false;
  if (response.cannot_complete) {
    return hasCannotCompleteReason(response.cannot_complete_reason);
  }
  return response.response !== null;
}

export function collectionHasCannotCompleteWithoutReason(
  responses: Record<string | number, { cannot_complete?: boolean; cannot_complete_reason?: string } | undefined> | undefined,
): boolean {
  if (!responses) return false;
  return Object.values(responses).some((response) => isCannotCompleteWithoutReason(response));
}

/**
 * Next revision label. Version is stored as "Rev 1", so string concatenation
 * would produce "Rev 11".
 */
export function nextRevisionLabel(version: string | number | null | undefined): string {
  const text = String(version ?? '').trim();
  const match = text.match(/(\d+)\s*$/);
  const current = match ? Number(match[1]) : 0;
  const next = Number.isFinite(current) && current > 0 ? current + 1 : 1;
  return `Rev ${next}`;
}

/** Build a write payload without read-only nested fields (responses, sub_items, etc.) */
export function buildChecklistItemWritePayload(item: ChecklistItemLike): ChecklistItemWritePayload {
  return {
    ...(item.checklist !== undefined ? { checklist: item.checklist } : {}),
    ...(item.parent_item !== undefined ? { parent_item: item.parent_item } : {}),
    title: item.title,
    description: item.description ?? '',
    item_type: item.item_type,
    order: item.order,
    is_required: item.is_required,
    validation_rules: item.validation_rules ?? {},
    options: normalizeChecklistItemOptions(item.options),
    default_value: item.default_value ?? '',
    allowed_file_types: item.allowed_file_types ?? '',
    max_file_size_mb: item.max_file_size_mb ?? 10,
    is_visible: item.is_visible ?? true,
    show_when_parent_completed: item.show_when_parent_completed ?? false,
  };
}
